import { useTranslation } from "react-i18next"

interface ConfirmationModalProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  changesCount: number
}

export default function PermissionConfirmationModal({
  isOpen,
  onConfirm,
  onCancel,
  changesCount
}: ConfirmationModalProps) {
  const { t } = useTranslation()

  if (!isOpen) return null

  return (
    <div className="fixed top-0 left-0 z-999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
      <div className="w-full max-w-142.5 rounded-lg bg-white py-12 px-8 text-center dark:bg-boxdark md:py-15 md:px-17.5">
        <span className="mx-auto inline-block">
          <svg
            width="60"
            height="60"
            viewBox="0 0 60 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              opacity="0.1"
              width="60"
              height="60"
              rx="30"
              fill="#DC2626"
            ></rect>
            <path
              d="M30 27.2498V29.9998V27.2498ZM30 35.4999H30.0134H30ZM20.6914 41H39.3086C41.3778 41 42.6704 38.7078 41.6358 36.8749L32.3272 20.3747C31.2926 18.5418 28.7074 18.5418 27.6728 20.3747L18.3642 36.8749C17.3296 38.7078 18.6222 41 20.6914 41Z"
              stroke="#DC2626"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
          </svg>
        </span>
        <h4 className="mt-5.5 pb-2 text-xl font-bold text-black dark:text-white sm:text-2xl">
          Confirm Permission Changes?
        </h4>
        <p className="mb-5 font-medium dark:text-white">
          You are about to update permissions for {changesCount} role{changesCount !== 1 ? 's' : ''}.
        </p>
        <div className="rounded-sm border border-warning bg-warning/10 p-4 mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <strong className="text-warning">⚠️ Security Warning:</strong> Changing permissions affects what users can access and modify.
            Incorrect permissions could grant unauthorized access or block legitimate users.
          </p>
        </div>
        <div className="rounded-sm border border-stroke bg-gray-2 dark:bg-meta-4 p-4 mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-left">
            <strong>Changes will affect:</strong>
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 text-left list-disc ml-6 mt-2">
            <li>What modules each role can access</li>
            <li>Whether users can view, create, edit, or delete data</li>
            <li>Discord bot API access permissions</li>
          </ul>
        </div>
        <p className="mb-10 text-sm font-medium dark:text-white">
          Are you sure you want to save these changes?
        </p>
        <div className="-mx-3 flex flex-row gap-y-4">
          <div className="w-full px-3 2xsm:w-1/2">
            <button
              onClick={onCancel}
              className="block w-full rounded border border-stroke bg-gray py-3 px-6 text-center font-medium text-black transition hover:border-meta-1 hover:bg-meta-1 hover:text-white dark:border-strokedark dark:bg-meta-4 dark:text-white dark:hover:border-meta-1 dark:hover:bg-meta-1"
            >
              Cancel
            </button>
          </div>
          <div className="w-full px-3 2xsm:w-1/2">
            <button
              onClick={onConfirm}
              className="block w-full rounded border border-primary bg-primary py-3 px-6 text-center font-medium text-white transition hover:bg-opacity-90"
            >
              Confirm & Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
